import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatEUR, formatDate } from "@/lib/constants";
import { toast } from "sonner";
import {
  ArrowLeft, Bot, Search, FileText, CheckCircle, Send, Repeat, Flame, Trophy, XCircle,
  Mail, AlertCircle, LifeBuoy, PauseCircle, PlayCircle, Sparkles, Boxes, Zap, ExternalLink
} from "lucide-react";
import LeadEmailFollowupCard from "@/components/LeadEmailFollowupCard";
import { SolutionBadge, PriorityBadge, TemperatureBadge, EstimateStatusBadge, QueueStatusBadge, HotLeadBadge, TimelineBadge, QUEUE_LABEL, SOLUTION_LABEL } from "@/components/EnergyBadges";

export default function LeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [objText, setObjText] = useState("");
  const [objResult, setObjResult] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get(`/leads/${id}`);
    setData(r.data);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const action = async (key, fn, success) => {
    setBusy(key);
    try { await fn(); toast.success(success); await load(); }
    catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.error === "lead_limit_reached") {
        toast.error(detail.message || "Leadlimit erreicht – bitte Upgrade anfragen.", { duration: 6000 });
        nav("/settings");
      } else {
        toast.error(typeof detail === "string" ? detail : "Fehler");
      }
    }
    finally { setBusy(""); }
  };

  if (loading || !data) return <div className="text-slate-500">Wird geladen …</div>;
  const { lead, messages, offers, followups, objections, interactions } = data;
  const latestOffer = offers?.[offers.length - 1];

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              {lead.first_name} {lead.last_name}
            </h1>
            {lead.is_hot && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-100 border border-orange-300 text-orange-900 text-xs font-bold"><Flame className="w-3 h-3" /> Sofort-Lead</span>}
            {lead.is_reactivated && <span data-testid="badge-reactivated" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold"><Sparkles className="w-3 h-3" /> Reaktiviert</span>}
            {lead.recovered_revenue > 0 && <span data-testid="badge-recovered" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold"><LifeBuoy className="w-3 h-3" /> Geretteter Umsatz {formatEUR(lead.recovered_revenue)}</span>}
            {lead.source === "configurator" && <span data-testid="badge-source-configurator" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold"><Boxes className="w-3 h-3" /> Konfigurator</span>}
          </div>
          <p className="mt-1 text-slate-600">{lead.email} · {lead.phone}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={lead.status} testid="lead-status-badge" />
          <span className="text-sm text-slate-500">· Wert {formatEUR(lead.estimated_value)}</span>
        </div>
      </div>

      {lead.is_hot && (
        <Card data-testid="hot-lead-alert" className="border-orange-200 bg-orange-50/60 p-5">
          <div className="flex items-start gap-3">
            <Flame className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold text-orange-900">Sofort-Lead: bitte umgehend melden</p>
              {(lead.hot_lead_reason || lead.reactivation_reason) && (
                <p className="text-sm text-orange-800 mt-1">Grund: {lead.hot_lead_reason || lead.reactivation_reason}</p>
              )}
              {lead.reactivated_by && <p className="text-xs text-orange-700 mt-1">Reaktiviert durch: {lead.reactivated_by} · {formatDate(lead.reactivated_at)}</p>}
            </div>
          </div>
        </Card>
      )}

      {lead.recovered_revenue > 0 && (
        <Card data-testid="recovered-info-card" className="border-emerald-200 bg-emerald-50/60 p-5">
          <div className="flex items-start gap-3">
            <LifeBuoy className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-emerald-900">Geretteter Umsatz: {formatEUR(lead.recovered_revenue)}</p>
              <p className="text-sm text-emerald-800 mt-0.5">Quelle: {lead.recovered_source || "—"} · erfasst {formatDate(lead.recovered_at)}</p>
            </div>
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        <ActionBtn testid="action-analyze" icon={Search} label="Fehlende Daten prüfen" onClick={() => action("a", () => api.post(`/leads/${id}/analyze`), "Bedarfsanalyse erstellt")} busy={busy === "a"} />
        <ActionBtn testid="action-question" icon={Bot} label="Rückfrage generieren" onClick={() => action("q", () => api.post(`/leads/${id}/generate-question`), "Rückfrage erstellt")} busy={busy === "q"} />
        <ActionBtn testid="action-offer" icon={FileText} label="Angebotsentwurf erstellen" onClick={() => action("o", () => api.post(`/leads/${id}/generate-offer`), "Angebotsentwurf erstellt")} busy={busy === "o"} />
        {latestOffer && latestOffer.status === "Entwurf" && (
          <ActionBtn testid="action-approve" icon={CheckCircle} label="Angebot freigeben" onClick={() => action("ap", () => api.post(`/offers/${latestOffer.offer_id}/approve`), "Freigegeben")} busy={busy === "ap"} />
        )}
        {latestOffer && (latestOffer.status === "Freigegeben" || latestOffer.status === "Entwurf") && (
          <ActionBtn testid="action-send" icon={Send} label="Angebot versenden" onClick={() => action("s", () => api.post(`/offers/${latestOffer.offer_id}/send`, { origin_url: window.location.origin }), "Angebot versendet")} busy={busy === "s"} />
        )}
        {latestOffer && latestOffer.status === "Versendet" && !lead.followup_paused && followups.length === 0 && (
          <ActionBtn testid="action-followup" icon={Repeat} label="Nachfassung starten" onClick={() => action("f", () => api.post(`/leads/${id}/start-followup`), "Nachfass-Sequenz aktiv")} busy={busy === "f"} />
        )}
        {followups.length > 0 && !lead.followup_paused && (
          <ActionBtn testid="action-pause-followup" icon={PauseCircle} label="Nachfassung pausieren" onClick={() => action("pf", () => api.post(`/leads/${id}/pause-followup`), "Nachfassung pausiert")} busy={busy === "pf"} />
        )}
        {lead.followup_paused && (
          <ActionBtn testid="action-resume-followup" icon={PlayCircle} label="Nachfassung fortsetzen" onClick={() => action("rf", () => api.post(`/leads/${id}/resume-followup`), "Nachfassung fortgesetzt")} busy={busy === "rf"} />
        )}
        <ActionBtn testid="action-hot" icon={Flame} label="Hot Lead" onClick={() => action("h", () => api.post(`/leads/${id}/mark-hot`), "Als Hot Lead markiert")} busy={busy === "h"} />
        <ActionBtn testid="action-won" icon={Trophy} label="Gewonnen" onClick={() => action("w", () => api.post(`/leads/${id}/mark-won`), "Gewonnen")} busy={busy === "w"} variant="success" />
        <ActionBtn testid="action-lost" icon={XCircle} label="Verloren" onClick={() => action("l", () => api.post(`/leads/${id}/mark-lost`), "Verloren")} busy={busy === "l"} variant="danger" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="overview">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger data-testid="tab-overview" value="overview">Übersicht</TabsTrigger>
              <TabsTrigger data-testid="tab-offer" value="offer">Angebot</TabsTrigger>
              <TabsTrigger data-testid="tab-comm" value="comm">Kommunikation</TabsTrigger>
              <TabsTrigger data-testid="tab-followup" value="followup">Nachfassung</TabsTrigger>
              <TabsTrigger data-testid="tab-objection" value="objection">Einwand</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4 mt-4">
              <Card className="border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Kundendaten</h3>
                <Grid items={[
                  ["Adresse", lead.address || "—"],
                  ["Immobilientyp", lead.property_type || "—"],
                  ["Dachtyp", lead.roof_type || "—"],
                  ["Dachfläche", lead.roof_area ? `${lead.roof_area} m²` : "—"],
                  ["Dachausrichtung", lead.roof_orientation || "—"],
                  ["Jahresverbrauch", lead.annual_consumption ? `${lead.annual_consumption} kWh` : "—"],
                  ["Speicher", lead.storage_wanted || "—"],
                  ["Wallbox", lead.wallbox_wanted || "—"],
                  ["Umsetzung", lead.timeframe || "—"],
                ]} />
                {lead.notes && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Bemerkungen</p>
                    <p className="text-sm text-slate-700">{lead.notes}</p>
                  </div>
                )}
              </Card>

              {/* Energie-Erweiterung Phase E — Produktpfad/Priorität/Estimate/Queue */}
              <Card className="border-slate-200 p-6" data-testid="energy-profile-card">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-orange-600" />
                  <h3 className="text-lg font-semibold text-slate-900">Energie-Profil</h3>
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  <SolutionBadge value={lead.solution_type} testid="detail-solution-badge" />
                  <PriorityBadge value={lead.priority} testid="detail-priority-badge" />
                  <TemperatureBadge value={lead.lead_temperature} testid="detail-temperature-badge" />
                  <TimelineBadge value={lead.implementation_timeline} testid="detail-timeline-badge" />
                  {lead.estimate_status && <EstimateStatusBadge value={lead.estimate_status} testid="detail-estimate-badge" />}
                  <QueueStatusBadge value={lead.conversion_queue_status} testid="detail-queue-badge" />
                  {lead.is_hot && <HotLeadBadge reason={lead.hot_lead_reason} testid="detail-hot-badge" />}
                </div>
                <Grid items={[
                  ["Lösung", SOLUTION_LABEL[lead.solution_type || "pv"] || "—"],
                  ["Umsetzungszeitraum", timelineHumanLabel(lead.implementation_timeline)],
                  ["Priorität", priorityHumanLabel(lead.priority)],
                  ["Lead-Temperatur", lead.lead_temperature || "—"],
                  ["Estimate-Status", lead.estimate_status || "—"],
                  ["Queue-Status", QUEUE_LABEL[lead.conversion_queue_status] || lead.conversion_queue_status || "—"],
                ]} />
                {lead.next_action && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Nächste empfohlene Aktion</p>
                    <p className="text-sm text-slate-700" data-testid="detail-next-action">{lead.next_action}</p>
                  </div>
                )}
                {lead.estimate_id && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Preisschätzung</p>
                    <EstimateLink leadId={lead.lead_id} estimateId={lead.estimate_id} offers={offers} />
                  </div>
                )}
              </Card>

              {lead.source === "configurator" && (
                <Card className="border-slate-200 p-6" data-testid="configurator-payload-card">
                  <div className="flex items-center gap-2 mb-3">
                    <Boxes className="w-4 h-4 text-orange-600" />
                    <h3 className="text-lg font-semibold text-slate-900">Konfigurator-Daten</h3>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    Quelle: <span className="font-mono">{lead.source_detail || "—"}</span>
                    {lead.source_url && <> · von <a href={lead.source_url} target="_blank" rel="noreferrer" className="text-orange-700 hover:underline break-all">{lead.source_url}</a></>}
                  </p>
                  <ConfiguratorPayloadList payload={lead.configurator_payload || {}} />
                  {lead.consent_given && (
                    <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500" data-testid="consent-info">
                      Einwilligung erteilt: {formatDate(lead.consent_timestamp)} · Version {lead.consent_text_version || "—"}
                    </div>
                  )}
                </Card>
              )}

              {lead.ai_summary && (
                <Card className="border-slate-200 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Bot className="w-4 h-4 text-orange-600" />
                    <h3 className="text-lg font-semibold text-slate-900">KI-Zusammenfassung</h3>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{lead.ai_summary}</p>
                </Card>
              )}

              {lead.missing_fields?.length > 0 && (
                <Card className="border-amber-200 bg-amber-50 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-4 h-4 text-amber-700" />
                    <h3 className="text-base font-semibold text-amber-900">Fehlende Pflichtangaben</h3>
                  </div>
                  <ul className="list-disc list-inside text-sm text-amber-900 space-y-1">
                    {lead.missing_fields.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="offer" className="space-y-4 mt-4">
              {!latestOffer ? (
                <Card className="border-slate-200 p-10 text-center">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">Noch kein Angebotsentwurf erstellt.</p>
                </Card>
              ) : (
                <Card className="border-slate-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{latestOffer.title}</h3>
                      <p className="text-sm text-slate-500 mt-1">{latestOffer.summary}</p>
                    </div>
                    <StatusBadge status={latestOffer.status} />
                  </div>
                  <div className="space-y-2 mb-4">
                    {latestOffer.items.map((it) => (
                      <div key={it.title} className="flex justify-between py-2 border-b border-slate-100">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{it.title}</p>
                          <p className="text-xs text-slate-500">{it.description}</p>
                        </div>
                        <p className="text-sm tabular-nums font-medium">{formatEUR(it.total)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between font-bold text-slate-900 pt-2">
                    <span>Gesamtpreis</span><span>{formatEUR(latestOffer.total_price)}</span>
                  </div>
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 mt-4">
                    Dieser Entwurf muss vor Versand fachlich geprüft und freigegeben werden.
                  </p>
                  <div className="flex gap-2 mt-4">
                    <Button data-testid="open-offer-editor" variant="outline" onClick={() => nav(`/offers/${latestOffer.offer_id}/edit`)}>Bearbeiten</Button>
                    <Button data-testid="open-public-offer" variant="outline" onClick={() => window.open(`/public/offer/${latestOffer.public_id}`, "_blank")}>Vorschau (Kundenansicht)</Button>
                  </div>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="comm" className="space-y-3 mt-4">
              {messages.length === 0 ? (
                <Card className="border-slate-200 p-10 text-center text-slate-500">Noch keine Nachrichten.</Card>
              ) : messages.map((m) => (
                <Card key={m.msg_id} className={`border-slate-200 p-4 ${m.direction === "in" ? "bg-blue-50/40" : "bg-white"}`}>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                    <Mail className="w-3 h-3" />
                    <span className="font-semibold">{m.direction === "in" ? "Kunde" : "Sie"}</span> · {m.channel} · {formatDate(m.created_at)}
                  </div>
                  {m.subject && <p className="text-sm font-semibold text-slate-900 mb-1">{m.subject}</p>}
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{m.content}</p>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="followup" className="space-y-3 mt-4">
              {lead.followup_paused && (
                <Card data-testid="followup-paused-info" className="border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-center gap-2 text-amber-900">
                    <PauseCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Nachfassung ist aktuell pausiert. Neue Nachrichten werden nicht versendet, bis du fortsetzt.</span>
                  </div>
                </Card>
              )}
              {followups.length === 0 ? (
                <Card className="border-slate-200 p-10 text-center text-slate-500">Noch keine Nachfass-Sequenz aktiv.</Card>
              ) : followups.map((f) => {
                const isPaused = f.status === "paused" || f.paused;
                const isSent = f.status === "sent";
                return (
                <Card key={f.followup_id} className={`border-slate-200 p-4 ${isPaused ? "bg-amber-50/40 border-amber-200" : ""}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-slate-900">Tag {f.day}: {f.subject}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isSent ? "bg-emerald-50 text-emerald-700" : isPaused ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                      {isSent ? "Versendet" : isPaused ? "Pausiert" : "Geplant"}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{f.message}</p>
                  <p className="text-xs text-slate-400 mt-2">Geplant: {formatDate(f.scheduled_for)}</p>
                  {!isSent && !isPaused && (
                    <Button
                      data-testid={`send-followup-${f.followup_id}`}
                      size="sm"
                      onClick={() => action(`fup-${f.followup_id}`, () => api.post(`/followups/${f.followup_id}/send`), "Nachfass-Nachricht versendet")}
                      className="mt-3 bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      Jetzt versenden
                    </Button>
                  )}
                </Card>
                );
              })}
            </TabsContent>

            <TabsContent value="objection" className="space-y-4 mt-4">
              <Card className="border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Kundenantwort eingeben</h3>
                <p className="text-sm text-slate-500 mb-4">Die KI erkennt den Einwand und schlägt eine Antwort vor.</p>
                <Textarea
                  data-testid="objection-input"
                  rows={4}
                  value={objText}
                  onChange={(e) => setObjText(e.target.value)}
                  placeholder="z.B. Ist mir ehrlich gesagt zu teuer."
                />
                <Button
                  data-testid="objection-analyze-btn"
                  onClick={async () => {
                    if (!objText.trim()) return;
                    setBusy("obj");
                    try {
                      const r = await api.post(`/leads/${id}/handle-objection`, { text: objText });
                      setObjResult(r.data);
                      toast.success(`Einwand erkannt: ${r.data.objection_type}`);
                      await load();
                    } catch { toast.error("Fehler"); }
                    finally { setBusy(""); }
                  }}
                  disabled={busy === "obj"}
                  className="mt-3 bg-slate-900 hover:bg-slate-800 text-white"
                >
                  {busy === "obj" ? "Analysiere …" : "Einwand analysieren"}
                </Button>
              </Card>

              {objResult && (
                <Card className="border-emerald-200 bg-emerald-50 p-6">
                  <p className="text-xs uppercase tracking-wider font-semibold text-emerald-800 mb-2">Erkannter Einwand: {objResult.objection_type}</p>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{objResult.suggested_reply}</p>
                </Card>
              )}

              <div className="space-y-3">
                {objections.map((o) => (
                  <Card key={o.obj_id} className="border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">{o.objection_type} · {formatDate(o.created_at)}</p>
                    <p className="text-sm text-slate-700 italic mt-1">„{o.customer_text}"</p>
                    <p className="text-sm text-slate-900 mt-2"><span className="font-semibold">Vorschlag:</span> {o.suggested_reply}</p>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <Card className="border-slate-200 p-6">
            <h3 className="text-base font-semibold text-slate-900 mb-3">Nächste Aktion</h3>
            <p className="text-sm text-slate-700">{lead.next_action || "Keine Aktion empfohlen."}</p>
          </Card>

          <LeadEmailFollowupCard offers={offers} messages={messages} onChanged={load} />

          <Card className="border-slate-200 p-6">
            <h3 className="text-base font-semibold text-slate-900 mb-3">Interne Notizen</h3>
            <Textarea
              data-testid="internal-notes"
              rows={6}
              value={lead.internal_notes || ""}
              onChange={(e) => setData((d) => ({ ...d, lead: { ...d.lead, internal_notes: e.target.value } }))}
              onBlur={async (e) => {
                try {
                  await api.patch(`/leads/${id}`, { internal_notes: e.target.value });
                } catch (err) {
                  if (process.env.NODE_ENV !== "production") console.error("Saving notes failed", err);
                }
              }}
            />
          </Card>

          {interactions.length > 0 && (
            <Card className="border-slate-200 p-6">
              <h3 className="text-base font-semibold text-slate-900 mb-3">Aktivitäten</h3>
              <ul className="space-y-2 text-sm">
                {interactions.slice(0, 8).map((i) => (
                  <li key={i.interaction_id} className="flex justify-between text-slate-700">
                    <span>{interactionLabel(i.type)}</span>
                    <span className="text-slate-400 text-xs">{formatDate(i.created_at)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

const ActionBtn = ({ icon: Icon, label, onClick, busy, variant, testid }) => {
  const cls = variant === "success"
    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
    : variant === "danger"
    ? "bg-red-600 hover:bg-red-700 text-white"
    : "bg-white hover:bg-slate-50 text-slate-900 border border-slate-200";
  return (
    <button data-testid={testid} onClick={onClick} disabled={busy} className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${cls} disabled:opacity-50`}>
      <Icon className="w-4 h-4" />
      {busy ? "…" : label}
    </button>
  );
};

const Grid = ({ items }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {items.map(([k, v]) => (
      <div key={k}>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{k}</p>
        <p className="text-sm text-slate-900 mt-0.5">{v}</p>
      </div>
    ))}
  </div>
);

// Energie-Erweiterung Phase E — Helpers für menschliche Labels
function timelineHumanLabel(t) {
  return {
    immediate: "Sofort",
    under_3_months: "In den nächsten 3 Monaten",
    over_3_months: "Später als 3 Monate",
    info_only: "Möchte sich informieren",
  }[t] || (t ? t : "—");
}

function priorityHumanLabel(p) {
  return {
    urgent: "Dringend (Sofort melden)",
    high: "Hoch (zeitnah kontaktieren)",
    normal: "Standard",
    low: "Niedrig (nurturen)",
  }[p] || (p || "—");
}

function EstimateLink({ leadId, estimateId, offers }) {
  const offer = (offers || []).find((o) => o.offer_id === estimateId);
  if (!offer || !offer.public_id) {
    return <p className="text-xs text-slate-500" data-testid="detail-estimate-id">Estimate-ID: <span className="font-mono">{estimateId}</span></p>;
  }
  return (
    <a
      data-testid="detail-estimate-link"
      href={`/public/offer/${offer.public_id}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 text-sm text-orange-700 hover:text-orange-800 font-medium"
    >
      Preisschätzung öffnen <ExternalLink className="w-3.5 h-3.5" />
    </a>
  );
}

const PAYLOAD_LABELS = {
  zip: "PLZ", city: "Ort", street: "Straße", property_type: "Gebäudetyp",
  annual_consumption: "Jahresverbrauch (kWh)", household_size: "Haushaltsgröße",
  has_heatpump: "Wärmepumpe", has_ev: "E-Auto",
  roof_type: "Dachtyp", roof_area: "Dachfläche (m²)", roof_orientation: "Dachausrichtung",
  shading: "Verschattung", ownership: "Eigentümerstatus",
  wants_pv: "PV-Anlage gewünscht", wants_storage: "Speicher gewünscht",
  wants_wallbox: "Wallbox gewünscht", wants_backup: "Notstrom gewünscht",
  wants_consultation: "Beratung gewünscht", timeframe: "Umsetzungszeitraum",
  first_name: "Vorname", last_name: "Nachname",
  email: "E-Mail", phone: "Telefon", preferred_channel: "Kontaktweg", notes: "Nachricht",
  source_url: "Quell-URL", consent_text_version: "Datenschutz-Version",
};
function fmtPayloadVal(v) {
  if (v === true) return "Ja";
  if (v === false) return "Nein";
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}
const ConfiguratorPayloadList = ({ payload }) => {
  const entries = Object.entries(payload || {}).filter(([k]) => k !== "source_url");
  if (entries.length === 0) {
    return <p className="text-sm text-slate-500" data-testid="configurator-payload-empty">Keine zusätzlichen Daten aus dem Konfigurator vorhanden.</p>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="configurator-payload-list">
      {entries.map(([k, v]) => (
        <div key={k} data-testid={`configurator-payload-${k}`}>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{PAYLOAD_LABELS[k] || k}</p>
          <p className="text-sm text-slate-900 mt-0.5 break-words">{fmtPayloadVal(v)}</p>
        </div>
      ))}
    </div>
  );
};

const interactionLabel = (type) => ({
  offer_opened: "Angebot geöffnet",
  book_call: "Rückruf gebucht",
  confirm_interest: "Interesse bestätigt",
  ask_question: "Frage gestellt",
  hot_lead_detected: "Hot Lead erkannt",
  lead_reactivated: "Lead reaktiviert",
  followup_paused: "Nachfassung pausiert",
  followup_resumed: "Nachfassung fortgesetzt",
  recovered_revenue_captured: "Geretteter Umsatz erfasst",
}[type] || type);
